// src/pages/bureau/components/BureauMembers.tsx
import React from "react";
import imgMatthieu from "@/assets/bureau/matthieu-lassalle.jpg";
import imgThierry  from "@/assets/bureau/thierry-daubercies.jpg";
import imgMichael  from "@/assets/bureau/michael-raynaud.jpg";
import imgThomas   from "@/assets/bureau/thomas-lecuyer.jpg";
import imgKewin    from "@/assets/bureau/kewin.jpg";

/**
 * Assure-toi que ton projet sert bien en UTF-8 :
 * - index.html → <meta charset="UTF-8" />
 */

type Member = {
  id: number;
  name: string;
  role: string;
  email?: string;
  phone?: string;
  experience?: string;
  description: string;
  image: string;
  responsibilities: string[];
  focal?: string; // optionnel : "50% 40%" pour ajuster le cadrage (object-position)
};

export const BureauMembers: React.FC = () => {
  const members: Member[] = [
    {
      id: 1,
      name: "Matthieu Lassalle",
      role: "Président",
      email: "matthieu.lassalle93@gmail.com",
      phone: "06 12 31 06 51",
      experience: "10 ans au club",
      description:
        "Passionné de volley-ball, Matthieu dirige le club avec vision et détermination.",
      image: imgMatthieu,
      responsibilities: ["Direction générale", "Relations institutionnelles", "Stratégie du club"],
    },
    {
      id: 2,
      name: "Thierry Daubercies",
      role: "Vice-Président",
      email: "contact@volleyball-viarmes.fr",
      phone: "06 72 70 67 30",
      experience: "30 ans au club",
      description: "Thierry apporte son expertise technique et sa passion du sport.",
      image: imgThierry,
      responsibilities: ["Coordination sportive", "Relations avec les entraîneurs", "Développement des équipes"],
    },
    {
      id: 3,
      name: "Michaël Raynaud",
      role: "Trésorier",
      email: "contact@volleyball-viarmes.fr",
      experience: "20 ans au club",
      description:
        "Comptable de profession, Michaël assure la gestion financière rigoureuse du club.",
      image: imgMichael,
      responsibilities: ["Gestion financière", "Budget et comptabilité", "Subventions et partenariats"],
    },
    {
      id: 4,
      name: "Thomas Lecuyer",
      role: "Secrétaire Général",
      email: "contact@volleyball-viarmes.fr",
      experience: "20 ans au club",
      description:
        "Organisé et efficace, Thomas coordonne toutes les activités administratives du club.",
      image: imgThomas,
      responsibilities: ["Administration générale", "Communication interne", "Gestion des licences"],
    },
    {
      id: 6,
      name: "Kewin Hamadi",
      role: "Responsable Communication / Webmaster",
      email: "contact@volleyball-viarmes.fr",
      phone: "06 41 75 48 77",
      experience: "10 ans au club",
      description:
        "Spécialiste en communication digitale, il développe la visibilité du club.",
      image: imgKewin,
      responsibilities: ["Administration générale", "Communication interne", "Gestion des licences"],
    },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Membres du Bureau</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une équipe dirigeante expérimentée et passionnée, dédiée au développement
            et à la réussite du Club Sportif Volley-Ball de Viarmes (CSVBV).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {members.map((member) => (
            <div key={member.id} className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="relative">
                <img
                  src={member.image}
                  alt={`Portrait de ${member.name}`}
                  className="w-full aspect-[3/4] object-cover object-center"
                  style={member.focal ? { objectPosition: member.focal } : undefined}
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <h3 className="text-lg font-bold text-white">{member.name}</h3>
                  <p className="text-blue-200 text-sm font-medium">{member.role}</p>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{member.description}</p>

                <div className="space-y-2 mb-4">
                  {member.experience && (
                    <div className="flex items-center text-gray-600">
                      <i className="ri-time-line mr-2 text-blue-600 w-5"></i>
                      <span className="text-sm">{member.experience}</span>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center text-gray-600">
                      <i className="ri-mail-line mr-2 text-blue-600 w-5"></i>
                      <a href={`mailto:${member.email}`} className="text-sm hover:text-blue-600 transition-colors">
                        {member.email}
                      </a>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center text-gray-600">
                      <i className="ri-phone-line mr-2 text-blue-600 w-5"></i>
                      <a href={`tel:${member.phone.replace(/\s+/g, "")}`} className="text-sm hover:text-blue-600 transition-colors">
                        {member.phone}
                      </a>
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Responsabilités :</h4>
                  <ul className="space-y-1">
                    {member.responsibilities.map((responsibility, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <i className="ri-check-line mr-2 text-green-500 w-4"></i>
                        {responsibility}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">CSVBV</span>
                  <i className="ri-volleyball-line text-blue-600 text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-blue-50 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Rejoindre le Bureau</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Vous souhaitez vous impliquer davantage dans la vie du club ? Le bureau est toujours à la recherche de bénévoles motivés pour enrichir notre équipe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:contact@volleyball-viarmes.fr" className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 whitespace-nowrap">
                <i className="ri-mail-line mr-2"></i>
                Candidater
              </a>
              <a href="/contact" className="inline-flex items-center justify-center px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors durée-200 whitespace-nowrap">
                <i className="ri-information-line mr-2"></i>
                Plus d'informations
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
